import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Radio, X, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 60_000;
const DISMISS_KEY = "summit_live_webinar_dismissed";

/**
 * Site-wide live-webinar banner + toast.
 *
 * Runs inside the authenticated dashboard shell only. Polls /webinars/live
 * every 60s. On first sighting of a NEW live webinar id (not equal to the
 * previously-tracked id in localStorage), we pop a sonner toast so members
 * currently on any page get notified. The banner itself pins to the top of
 * the shell and can be dismissed for the current session.
 */
export default function LiveWebinarBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [live, setLive] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; }
  });
  const lastSeenIdRef = useRef(null);

  // Only members with paid access can see this.
  const enabled = user && (user.role === "admin"
    || user.tier === "basic" || user.tier === "premium");

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const check = async () => {
      try {
        const r = await api.get("/webinars/live");
        if (cancelled) return;
        const w = r.data?.webinar || null;
        setLive(w);

        if (w && w.id !== lastSeenIdRef.current) {
          // A new live session appeared while the user is browsing —
          // pop a one-time toast (skip on first mount to avoid noise
          // for a webinar that's been live for a while).
          if (lastSeenIdRef.current) {
            toast(
              <span className="inline-flex items-center gap-2">
                <Radio size={14} className="text-rose-500" />
                <span>
                  <b>{w.title}</b> {t("liveBannerUi.justWentLive")}
                </span>
              </span>,
              { duration: 8000 },
            );
            // Clear any old dismissal so the new webinar shows the banner
            try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
            setDismissed(null);
          }
          lastSeenIdRef.current = w.id;
        } else if (!w) {
          lastSeenIdRef.current = null;
        }
      } catch { /* ignore poll failures */ }
    };

    check();
    const id = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);

  if (!enabled || !live) return null;
  if (dismissed && dismissed === live.id) return null;
  // Hide the banner on the Webinars page itself — the inline live banner
  // already covers it there.
  if (pathname.startsWith("/resources") && (
    (typeof window !== "undefined" && window.location.search.includes("tab=webinars"))
  )) return null;

  const doDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, live.id); } catch { /* ignore */ }
    setDismissed(live.id);
  };

  return (
    <div
      data-testid="global-live-webinar-banner"
      className="mb-4 relative overflow-hidden rounded-xl border border-rose-500/40 bg-gradient-to-br from-rose-950 via-slate-950 to-slate-950 text-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-rose-300 font-bold">
            {t("liveBannerUi.liveNow")}
          </div>
          <div className="text-sm font-semibold text-white truncate">
            {live.title}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/resources?tab=webinars"
          data-testid="global-live-webinar-cta"
          className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-bold text-xs h-9 px-4 rounded-md"
        >
          {t("liveBannerUi.join")} <ExternalLink size={12} />
        </Link>
        <button
          type="button"
          onClick={doDismiss}
          data-testid="global-live-webinar-dismiss"
          aria-label={t("liveBannerUi.dismiss")}
          className="w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
